import {Phone, Calendar, Shield, Sparkles, Car, Award, Check, Zap, MapPin} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'

const PpfLaJollaPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does PPF cost in La Jolla?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front PPF starts at $1,299, full front starts at $1,899, and full vehicle coverage starts at $4,499. Pricing varies by vehicle make, model, and paint condition. We provide transparent quotes for all La Jolla customers."
          }
        },
        {
          "@type": "Question",
          "name": "How long does PPF last in La Jolla's coastal climate?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Premium PPF lasts 10+ years even in La Jolla's salt air and coastal environment. Self-healing technology and UV-resistant properties ensure long-term protection against sun damage, ocean spray, and environmental contaminants."
          }
        },
        {
          "@type": "Question",
          "name": "Does PPF protect against La Jolla beach parking damage?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. PPF prevents door dings, shopping cart scratches, and minor parking lot impacts common in La Jolla Shores and Village parking areas. Self-healing film repairs light scratches with heat exposure."
          }
        },
        {
          "@type": "Question",
          "name": "Can you install PPF on luxury vehicles in La Jolla?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. We specialize in high-end vehicle protection for Porsche, Tesla, BMW, Mercedes, Range Rover, and other luxury brands popular in La Jolla. Our installers have extensive experience with exotic and luxury automotive paint protection."
          }
        }
      ]
    })
    document.head.appendChild(script)

    document.title = "Paint Protection Film La Jolla | PPF Installation for Luxury & Tesla Vehicles"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert paint protection film installation in La Jolla, CA. Protect Tesla, Porsche, BMW, Mercedes with professional PPF. Self-healing technology, manufacturer-backed film warranties. Serving La Jolla Shores, UTC, and coastal San Diego.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/ppf-la-jolla" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Paint Protection Film in La Jolla, CA</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your luxury vehicle investment with professional paint protection film installation in La Jolla, California. Our installers deliver careful PPF coverage for Tesla, Porsche, BMW, Mercedes, Range Rover, and exotic vehicles—preserving showroom finish against coastal salt air, UV damage, and road debris.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Located just minutes from La Jolla Village, UTC, and Torrey Pines, we serve discerning vehicle owners throughout coastal San Diego with bulk or precision-pattern installation, self-healing technology, and manufacturer-backed warranties. Whether you're protecting a new Tesla delivery or preserving a classic Porsche, our La Jolla PPF services maintain value and appearance for years.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Luxury Vehicle Protection */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Luxury Vehicle Paint Protection in La Jolla</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              La Jolla's prestigious automotive community demands exceptional paint protection for high-end vehicles. We specialize in PPF installation for Porsche 911, Cayenne, Macan, and Panamera models popular throughout La Jolla Village and UTC. BMW M3, M5, X5, and X7 owners trust our installers for precision coverage that maintains factory paint perfection. Mercedes-Benz AMG variants, G-Wagon, S-Class, and E-Class vehicles receive expert attention worthy of their six-figure valuations.
            </p>
            <p>
              <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla paint protection</a> represents our highest volume luxury service, with Model S Plaid, Model X, Model 3 Performance, and Model Y protecting from La Jolla's coastal salt air. Audi RS models, Q7, Q8, and e-tron vehicles benefit from our bulk or precision-pattern methods selected for German engineering and complex bodywork. Range Rover, Bentley, Maserati, and exotic supercar owners receive white-glove service at our indoor Vista facility.
            </p>
            <p>
              Luxury vehicles in La Jolla face unique protection challenges. Thin modern paint systems, complex body lines, and expensive OEM paint replacement costs make PPF installation essential rather than optional. Our experience with high-end automotive paint ensures proper preparation, flawless installation, and long-term protection that preserves your investment in North County San Diego's most prestigious coastal community.
            </p>
          </div>
        </div>
      </section>

      {/* Why La Jolla Needs PPF */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why La Jolla Vehicle Owners Choose Paint Protection Film</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              La Jolla's coastal environment presents unique challenges for automotive paint protection. Salt-laden ocean breeze from La Jolla Shores and Windansea Beach, intense year-round UV exposure, and abrasive sand particles accelerate paint degradation, fading, and corrosion. <a href="/paint-protection-film" className="text-cyan-400 hover:underline">Paint protection film</a> creates an invisible barrier that preserves factory finish and protects your investment in one of San Diego's most prestigious communities.
            </p>
            <p>
              Vehicle owners in La Jolla Village, Bird Rock, and the Golden Triangle drive some of California's finest automobiles—Tesla Model S and Model X, Porsche 911 and Cayenne, BMW M-series, Mercedes AMG, Range Rover, and exotic supercars. These high-value vehicles demand professional protection against the harsh coastal elements that threaten paint integrity daily. PPF installation prevents costly paint correction and maintains resale value in La Jolla's competitive luxury automotive market.
            </p>
            <p>
              Our Vista facility, conveniently located 30 minutes from La Jolla via I-5, specializes in luxury and exotic vehicle PPF installation. We use bulk film or precision-cut patterns selected for your vehicle's make and model, with wrapped edges where appropriate for complete coverage. Self-healing technology repairs minor scratches and swirl marks with heat, while hydrophobic top coats repel water, dirt, and environmental contaminants.
            </p>
            <p>
              La Jolla residents frequently combine <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF installation</a> with <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a> and <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> for comprehensive vehicle protection and aesthetic enhancement. Whether you're daily driving through La Jolla Parkway traffic or weekend cruising down Torrey Pines Road, PPF preserves your vehicle's pristine condition against rock chips, bug splatter, bird droppings, and parking lot damage common in coastal California communities.
            </p>
            <p>
              We understand La Jolla vehicle owners expect perfection. Our installations include thorough paint preparation, professional wet application techniques, and meticulous edge wrapping that eliminates visible seams or film lines. Every installation is backed by a 10-year manufacturer warranty against yellowing, cracking, peeling, and delamination, plus our workmanship guarantee covering any installation defects.
            </p>
          </div>
        </div>
      </section>

      {/* Tesla PPF for La Jolla */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Paint Protection Film for La Jolla Owners</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg mb-12">
            <p>
              La Jolla has one of the highest Tesla ownership rates in California, with Model 3, Model Y, Model S, and Model X vehicles filling driveways from Mount Soledad to La Jolla Shores. Tesla's water-based paint is thinner and more vulnerable to damage than traditional automotive paint, making <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">professional Tesla PPF installation</a> a critical investment for maintaining appearance and resale value.
            </p>
            <p>
              We install PPF on dozens of La Jolla Tesla vehicles monthly, using model-specific patterns that ensure perfect fitment for Model 3, Model Y, Model S, Model X, and Cybertruck. Our installation process protects Autopilot sensors, cameras, and trim pieces while delivering seamless coverage across all painted surfaces. Many La Jolla Tesla owners schedule PPF installation immediately after delivery to protect factory paint from day one.
            </p>
            <p>
              Popular Tesla PPF packages for La Jolla customers include full front coverage (hood, bumper, fenders, mirrors, headlights) starting at $1,899, and full vehicle coverage for maximum protection and resale value preservation starting at $4,499. We also offer partial coverage options for budget-conscious owners who primarily need highway rock chip protection.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Model-Specific Patterns</h3>
              <p className="text-gray-300">Bulk or precision-pattern methods selected for every Tesla model keep coverage complete without unnecessary cutting on your vehicle's paint.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Technology</h3>
              <p className="text-gray-300">Premium film heals minor scratches and swirl marks with heat, maintaining a flawless finish throughout La Jolla's sunny climate.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Manufacturer-Backed Warranty</h3>
              <p className="text-gray-300">Manufacturer warranty against yellowing, cracking, and peeling, plus installation workmanship guarantee.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PPF Coverage Options */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">PPF Coverage Packages for La Jolla Vehicles</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Front</h3>
              <p className="text-gray-300 mb-6">
                Essential coverage for high-impact zones: front bumper, partial hood, mirrors, and headlights. Protects against highway debris and daily driving wear.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,299</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Front bumper full coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Partial hood (12-18 inches)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Mirrors and headlights</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Full Front</h3>
              <p className="text-gray-300 mb-6">
                Complete front-end protection: full hood, bumper, fenders, mirrors, headlights. Maximum highway and coastal protection for La Jolla drivers.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,899</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full hood, fenders, and bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Headlights, fog lights, mirrors</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Manufacturer-backed film warranty</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle</h3>
              <p className="text-gray-300 mb-6">
                Ultimate protection covering every painted surface. Maintain showroom condition and maximize resale value for your luxury vehicle.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $4,499</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by vehicle</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All full front coverage areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Doors, rocker panels, quarters</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Roof, trunk, rear bumper</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm">
            Starting prices shown are for standard vehicles. Exact pricing will be determined after a free consultation and vehicle inspection. All packages include professional paint preparation, precision installation, and manufacturer warranty.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why La Jolla Drivers Trust Our PPF Installation</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Coastal Protection</h3>
              <p className="text-gray-300">Specialized protection against La Jolla's salt air, ocean spray, and marine layer moisture that accelerates paint oxidation.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Film</h3>
              <p className="text-gray-300">Advanced technology repairs light scratches from beach parking, shopping carts, and daily wear with sun heat activation.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Luxury Expertise</h3>
              <p className="text-gray-300">Professional installation for Porsche, Tesla, BMW, Mercedes, and exotic vehicles common in La Jolla's luxury automotive community.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Resale Value</h3>
              <p className="text-gray-300">Maintain pristine paint condition for maximum resale value in La Jolla's competitive high-end automotive market.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Professional PPF Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Consultation & Vehicle Assessment</h3>
                <p className="text-gray-300">We inspect your vehicle's paint condition, discuss coverage options, and provide a transparent quote. For La Jolla customers, we review coastal protection needs and recommend appropriate coverage based on driving habits, parking environment, and budget.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Paint Preparation & Correction</h3>
                <p className="text-gray-300">We thoroughly wash and decontaminate your vehicle using premium automotive shampoo and clay bar treatment. For vehicles with existing swirl marks, water spots, or light scratches, we perform paint correction to ensure a flawless surface before PPF application. Proper preparation is critical for long-term adhesion and bubble-free results.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Precision Installation, Selected by Vehicle and Panel</h3>
                <p className="text-gray-300">Using vehicle-specific patterns, we apply premium self-healing PPF with professional wet installation techniques. Our installers use proper squeegee methods, edge wrapping, and heat forming to achieve seamless coverage across complex curves and body lines. We never cut on the vehicle, eliminating risk of paint damage during installation.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Inspection & Delivery</h3>
                <p className="text-gray-300">Multi-point inspection ensures perfect edges, no bubbles, complete adhesion, and proper curing. We provide care instructions, warranty documentation, and maintenance recommendations before returning your vehicle in showroom condition. Most La Jolla customers are amazed they can't see or feel the film—only perfect paint protection.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Local Context */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Paint Protection for La Jolla's Unique Environment</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              La Jolla's coastal microclimate demands specialized automotive protection. Vehicles parked near La Jolla Cove, Windansea Beach, or La Jolla Shores experience constant salt air exposure that causes clear coat oxidation, paint fading, and accelerated corrosion. Morning marine layer deposits moisture and salt particles on paint surfaces, while intense afternoon sun bakes contaminants into the clear coat, creating permanent etching and water spotting.
            </p>
            <p>
              La Jolla Village parking lots, UTC shopping centers, and Westfield UTC garage environments expose vehicles to shopping cart impacts, door dings, and careless pedestrian contact. PPF absorbs these minor impacts and prevents paint chipping, scratching, and damage that would otherwise require expensive paint repair or full panel repainting. Self-healing properties allow the film to recover from light scratches that occur during normal La Jolla daily driving and parking.
            </p>
            <p>
              Driving down La Jolla Parkway, Torrey Pines Road, or commuting to UTC via I-5 exposes your vehicle to highway rock chips, gravel spray, and debris from construction vehicles. La Jolla's proximity to coastal construction projects and beach sand means airborne particles constantly bombard front-facing surfaces. PPF creates a sacrificial barrier that takes the impact, preventing permanent paint damage and maintaining your vehicle's pristine appearance.
            </p>
            <p>
              La Jolla residents also contend with bird droppings from coastal seagulls, tree sap from Torrey Pines, and acidic bug splatter from evening drives near Mission Bay. These environmental contaminants etch into unprotected clear coat within hours, causing permanent staining that requires professional paint correction. PPF's smooth, non-porous surface prevents adhesion and allows easy removal of contaminants without paint damage.
            </p>
            <p>
              Whether you live in Bird Rock, La Jolla Farms, Muirlands, or the Village, professional paint protection film installation preserves your vehicle's appearance and protects your investment in one of San Diego's most desirable coastal communities. Our Vista facility serves La Jolla customers with convenient scheduling, detailed consultations, and premium installation quality worthy of your luxury vehicle.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">La Jolla PPF FAQs</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does PPF cost in La Jolla?</h3>
              <p className="text-gray-300">Partial front PPF starts at $1,299, full front starts at $1,899, and full vehicle coverage starts at $4,499. Pricing varies by vehicle make, model, and paint condition. We provide transparent quotes for all La Jolla customers with no hidden fees or surprise charges.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does PPF last in La Jolla's coastal climate?</h3>
              <p className="text-gray-300">Premium PPF lasts 10+ years even in La Jolla's challenging salt air and coastal environment. Self-healing technology and UV-resistant properties ensure long-term protection against sun damage, ocean spray, and environmental contaminants. The film maintains optical clarity and adhesion throughout its lifespan when properly maintained.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does PPF protect against La Jolla beach parking damage?</h3>
              <p className="text-gray-300">Yes. PPF prevents door dings, shopping cart scratches, and minor parking lot impacts common in La Jolla Shores, Windansea, and Village parking areas. Self-healing film repairs light scratches with heat exposure, making it ideal for beach parking environments where contact damage is frequent.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can you install PPF on luxury vehicles in La Jolla?</h3>
              <p className="text-gray-300">Absolutely. We specialize in high-end vehicle protection for Porsche, Tesla, BMW, Mercedes, Range Rover, Lamborghini, Ferrari, McLaren, and other luxury and exotic brands popular in La Jolla. Our installers have extensive experience with complex body lines, exotic paint finishes, and manufacturer-specific installation requirements.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is PPF worth it for daily drivers in La Jolla?</h3>
              <p className="text-gray-300">Yes. Even daily commuters benefit from PPF protection against highway rock chips, parking lot damage, and environmental wear. The investment pays for itself by preventing paint repair costs and maintaining resale value. Many La Jolla drivers protect their vehicles immediately after purchase to preserve factory paint condition.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does PPF installation take?</h3>
              <p className="text-gray-300">Partial front coverage takes 1 day. Full front typically requires 2 days. Full vehicle PPF installation takes 3-5 days depending on vehicle size and complexity. We never rush installations—proper application and curing time ensures bubble-free, long-lasting results.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I wash my vehicle after PPF installation?</h3>
              <p className="text-gray-300">Wait 7 days before washing to allow complete curing. After the curing period, you can wash normally using touchless car washes or hand washing with pH-neutral soap. PPF's hydrophobic coating makes washing easier and helps maintain a cleaner finish longer in La Jolla's coastal environment.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Do you offer mobile PPF installation in La Jolla?</h3>
              <p className="text-gray-300">PPF installation requires a controlled indoor environment with proper lighting and a clean workspace. Mobile installation cannot achieve the same quality standards. We invite La Jolla customers to our Vista facility, just 30 minutes away, for professional installation in indoor conditions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Enhance your vehicle protection with complementary services:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="/paint-protection-film-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-400 text-sm">Full PPF pricing, coverage options, and installation guide for San Diego</p>
            </a>
            <a href="/tesla-ppf-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Tesla PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Specialized PPF installation for all Tesla models with precision-cut patterns</p>
            </a>
            <a href="/window-tint-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Window Tint San Diego</h3>
              <p className="text-gray-400 text-sm">Ceramic window tinting for UV protection, heat rejection, and privacy</p>
            </a>
            <a href="/chrome-delete-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Chrome Delete San Diego</h3>
              <p className="text-gray-400 text-sm">Vinyl chrome delete for blacked-out aesthetic and modern appearance</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Serving La Jolla & Coastal San Diego Communities
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Our Vista facility is conveniently located 30 minutes from La Jolla via I-5, serving vehicle owners throughout coastal San Diego with premium paint protection film installation.
          </p>
          <div className="grid md:grid-cols-2 gap-4 text-gray-300">
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">La Jolla Neighborhoods</h3>
              <ul className="space-y-1 text-sm">
                <li>• La Jolla Village</li>
                <li>• Bird Rock</li>
                <li>• La Jolla Shores</li>
                <li>• UTC / Golden Triangle</li>
                <li>• Muirlands</li>
                <li>• La Jolla Farms</li>
                <li>• Windansea</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Nearby Cities We Serve</h3>
              <ul className="space-y-1 text-sm">
                <li>• <a href="/ppf-del-mar" className="text-cyan-400 hover:underline">Del Mar</a></li>
                <li>• <a href="/ppf-solana-beach" className="text-cyan-400 hover:underline">Solana Beach</a></li>
                <li>• <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a></li>
                <li>• <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a></li>
                <li>• <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a></li>
                <li>• <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a></li>
                <li>• Pacific Beach</li>
              </ul>
            </div>
          </div>
          <p className="text-gray-300 text-center mt-8">
            <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Protect Your Vehicle with Professional PPF Installation</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free consultation and get a custom quote for paint protection film installation. Convenient appointments for La Jolla residents.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default PpfLaJollaPage
